import Text from '@deprecated-ui/common/Text';
import {
  ORDER_EXPIRY_TEXT,
  ORDER_STATUS_COLOR,
  ORDER_TYPE,
} from 'constants/trading';
import DayTradeBanner from 'features/day-trade/components/Banner';
import { trackOrderDetailShareSelected } from 'features/sharetrade/analytics/shareTradeShareTracker';
import useShareTradeModal from 'features/sharetrade/stores/useShareTradeModal';
import {
  getRemapOrderAction,
  getRemapOrderStatus,
} from 'features/sharetrade/utils/shareTradeOrder';
import NonSyariahBanner from 'features/syariah/components/NonSyariahBanner';
import If from 'global/ErrorHandlers/If';
import { expiryTypeById } from 'global/Securities/constants/transactionForm';
import useEmittenInfoStore from 'global/Securities/stores/useEmittenInfoStore';
import { StockType } from 'global/Securities/types/stockType';
import {
  ForcedSellMapper,
  OrderStateItem,
  PlatformOrderTypes,
} from 'global/Securities/types/store/order';
import { OrderAction, OrderStatus } from 'global/Securities/types/transaction';
import { getSellAmountInvestedFee } from 'global/Securities/utils/formula';
import { lotSize } from 'global/TradingModal/constant';
import useIsUserSyariah from 'global/TradingModal/hooks/useIsUserSyariah';
import useCalculateRealized from 'hooks/useCalculateRealized';
import useGetPortfolioData from 'hooks/useGetPortfolioData';
import useTradingAccount from 'hooks/useTradingAccount';
import { trackAutoBuyDetailView } from 'lib/analytics/autoOrder';
import { clevertapOrderDetailViewed } from 'lib/analytics/securities';
import {
  ORDER_EXPIRY,
  VIRTUAL_TRADING_TYPES,
} from 'lib/analytics/virtualTrading';
import tradingDataStore from 'lib/stores/tradingData';
import moment from 'moment';
import numeral from 'numeral';
import { FC, useEffect, useMemo } from 'react';
import useShareTradeShare from 'stores/sharetrade/useShareTradeShare';
import { formatNumber, isFloat } from 'utils/number';
import shallow from 'zustand/shallow';
import DetailModal from '../DetailModal';
import { iModal } from '../types';
import Realized from './Realized';
import {
  DetailOrderButton,
  OrderModalInfoTable,
  RejectedBox,
  ShareTradeButton,
} from './styled';
import useOrderDetailAction from './useOrderDetailAction';
import { ModalButtonContainerSecondary } from '../styled';

interface OrderDetailModalType extends iModal {
  data: OrderStateItem;
  onCancelOrder: () => void;
}
const formatDate = (date: string) => moment(date).format('DD MMM YY HH:mm:ss');

const Prefix: FC<{ status: string; message: string }> = ({
  status,
  message,
}) => (
  <If condition={status === 'REJECTED'}>
    <RejectedBox>
      <Text as="p" family="bold">
        Order Rejected
      </Text>
      <Text>{message}</Text>
    </RejectedBox>
  </If>
);

const OrderDetailModal: FC<OrderDetailModalType> = ({
  data,
  visible,
  onCancel,
  onCancelOrder,
}) => {
  const {
    action,
    symbol,
    order_total,
    price,
    status,
    gtc,
    label,
    config_type,
    trigger_price,
    amount_invested_fee,
    market_orderid,
    time_open,
    message,
    orderid,
    day_trade_is_day_trade,
    day_trade_force_sell_event,
    is_sharia: isStockSyariah,
    trailing_stop,
    platform_order_type,
  } = data;

  const { handleAmendOrderClick } = useOrderDetailAction({ data, onCancel });

  const isDayTrade = day_trade_is_day_trade;
  const isMarketOrder = platform_order_type === PlatformOrderTypes.FAK;

  const { portfolio } = tradingDataStore(
    (s) => ({
      portfolio: s.portfolio,
    }),
    shallow,
  );
  const { tradingbalance } = portfolio.data;
  const portfolioData = useGetPortfolioData(symbol, true, false, isDayTrade);
  const { isRealTrading } = useTradingAccount();
  const isUserSyariah = useIsUserSyariah();

  const emittenInfo = useEmittenInfoStore((s) => s.data);

  const [
    isShareTradeVisible,
    setIsShareTradeVisible,
    setSharedUsers,
    resetShareTradeModal,
  ] = useShareTradeModal(
    (s) => [
      s.isVisible,
      s.setIsVisible,
      s.setSharedUsers,
      s.resetShareTradeModal,
    ],
    shallow,
  );

  const setTransactionData = useShareTradeShare(
    (s) => s.setTransactionData,
    shallow,
  );

  const isDetaillVisible = useMemo(
    () => visible && !isShareTradeVisible,
    [visible, isShareTradeVisible],
  );

  const isShowShareButton = useMemo(
    () => data.status === OrderStatus.Match,
    [data.status],
  );

  // Realized Gain / Loss
  const { totalRealized, isLoss, realizedPercentage, showRealized } =
    useCalculateRealized(symbol, data.amount_matched_total, data.order_done);

  const handleShareTradeClick = () => {
    const sharesQty = order_total * lotSize;

    setTransactionData({
      order_id: orderid,
      price,
      symbol,
      status: getRemapOrderStatus(data.status as OrderStatus),
      type: getRemapOrderAction(data.action as OrderAction),
      order_time: new Date(
        `${data.time_match.replace(' ', 'T')}Z`,
      ).toISOString(),
      qty: sharesQty,
      total_investment: price * sharesQty,
    });

    trackOrderDetailShareSelected(
      action as OrderAction,
      orderid,
      symbol,
      emittenInfo.type_company?.toLowerCase() as StockType,
      expiryTypeById[gtc],
      message,
      status as OrderStatus,
    );

    setIsShareTradeVisible(true);
  };

  const handleModalCancel = () => {
    resetShareTradeModal();
    setSharedUsers({});
    onCancel();
  };

  useEffect(() => {
    if (data) {
      if (isRealTrading) {
        clevertapOrderDetailViewed({
          status,
          orderType: action,
          rejectReason: message,
          expiry:
            Number(gtc) === 1
              ? ORDER_EXPIRY.goodTillCanceled
              : ORDER_EXPIRY.goodForDay,
          symbol,
          type: VIRTUAL_TRADING_TYPES.realTrading,
          dayTrade: isDayTrade,
          dayTradeForceSell: !!ForcedSellMapper[day_trade_force_sell_event],
        });
      }

      trackAutoBuyDetailView();
    }
  }, [data]);

  const generatePorfolioInfo = () => {
    const buyLabel = isDayTrade ? 'In My Day Trade' : 'In My Portfolio';

    const portfolioInfo = [
      {
        key: 'title',
        label: <Text family="bold">{buyLabel}</Text>,
        value: '',
      },
      {
        key: 'tradingBalance',
        label: <Text>Trading Balance</Text>,
        value: <Text align="right">{formatNumber(tradingbalance)}</Text>,
      },
    ];

    if (action === ORDER_TYPE.SELL) {
      portfolioInfo[1] = {
        key: 'availableLot',
        label: (
          <Text>
            Available lot in{' '}
            <span style={{ fontWeight: 'bold' }}>{symbol}</span>
          </Text>
        ),
        value: (
          <Text align="right" weight="bold">
            {portfolioData?.available_lot ?? 0}
          </Text>
        ),
      };
    }

    return portfolioInfo;
  };

  const generateTitleBasedOnOrder = (key, orderType, addPrefix = false) => {
    const prefix = addPrefix && isMarketOrder ? 'Estimated ' : '';
    const keyByOrder = {
      investmentDone: {
        buy: 'Investment',
        sell: 'Proceeds',
      },
      investmentFeeDone: {
        buy: 'Investment (Plus Fee)',
        sell: 'Proceeds (Net Fee)',
      },
      investmentFeeInfo: {
        buy: 'Investment (Plus Fee)',
        sell: `${trailing_stop ? 'Estimated ' : ''}Proceeds (Net Fee)`,
      },
    };
    return prefix + (keyByOrder[key] ? keyByOrder[key][orderType] : '');
  };

  let expiry =
    Number(gtc) !== 0 ? ORDER_EXPIRY_TEXT.gtc : ORDER_EXPIRY_TEXT.gfd;
  let displayedPrice = formatNumber(price);

  const getEstimationFee = () => {
    if (trailing_stop) {
      return formatNumber(
        getSellAmountInvestedFee(
          order_total,
          price,
          [StockType.Waran, StockType.Right, StockType.Etf].includes(
            emittenInfo.type_company,
          ),
          day_trade_is_day_trade,
        ),
      );
    }
    return formatNumber(
      amount_invested_fee,
      true,
      amount_invested_fee % 1 === 0 ? '0,0' : '0,0.00',
    );
  };

  let estimation = getEstimationFee();

  if (isMarketOrder) {
    expiry = 'Fill and Kill';
    displayedPrice = 'Market';
    if (!amount_invested_fee || amount_invested_fee === 0) estimation = '-';
  }

  let orderInfo = [
    {
      key: 'status',
      label: 'Status',
      value: (
        <Text
          color={ORDER_STATUS_COLOR.order[status.toLowerCase()]}
          align="right"
          family="bold"
        >
          {status}
        </Text>
      ),
    },
    {
      key: 'expiry',
      label: 'Expiry',
      value: <Text align="right">{expiry}</Text>,
    },
  ];

  if (status === 'READY' && label === 'AB') {
    orderInfo = [
      ...orderInfo,
      {
        key: 'triggerPrice',
        label: 'Trigger Price',
        value: (
          <Text align="right">
            {config_type === '1' ? <span>&ge;</span> : <span>&le;</span>}{' '}
            {formatNumber(trigger_price)}
          </Text>
        ),
      },
    ];
  }

  orderInfo = [
    ...orderInfo,
    {
      key: 'price',
      label: 'Price',
      value: <Text align="right">{displayedPrice}</Text>,
    },
    {
      key: 'lot',
      label: 'Lot',
      value: <Text align="right">{order_total}</Text>,
    },
    ...(trailing_stop
      ? [
          {
            key: 'trailPercentage',
            label: 'Trail Percentage',
            value: (
              <Text align="right">
                {numeral(trailing_stop?.price_algorithm?.value).format(
                  '0,0.00',
                )}
                %
              </Text>
            ),
          },
          {
            key: 'stopPrice',
            label: 'Stop Price',
            value: (
              <Text align="right">
                {numeral(trailing_stop?.stop_price.units).format('0,0')}
              </Text>
            ),
          },
        ]
      : []),
    {
      key: 'investmentFeeInfo',
      label: generateTitleBasedOnOrder(
        'investmentFeeInfo',
        action.toLowerCase(),
        true,
      ),
      value: (
        <Text align="right" weight="bold">
          {estimation}
        </Text>
      ),
    },
    {
      key: 'idxOrderId',
      label: 'IDX Order ID',
      value: <Text align="right">{market_orderid ?? '-'}</Text>,
    },
    {
      key: 'orderTime',
      label: 'Order Time',
      value: (
        <Text align="right">{time_open ? formatDate(time_open) : '-'}</Text>
      ),
    },
  ];

  const generateOrderDone = (
    key,
    {
      price_average = 0,
      order_done = 0,
      amount_matched = 0,
      amount_matched_broker_fee = 0,
      amount_matched_exchange_fee = 0,
      amount_matched_total = 0,
    }: OrderStateItem,
  ) => {
    const config = {
      price: formatNumber(
        price_average,
        false,
        isFloat(price_average) ? '0,0.00' : '0,0',
      ),
      lot: order_done,
      investmentDone: formatNumber(amount_matched),
      brokerFee: formatNumber(amount_matched_broker_fee, true, '0,0.00'),
      exchangeFee: formatNumber(amount_matched_exchange_fee, true, '0,0.00'),
      infestmentFeeDone: formatNumber(amount_matched_total, true, '0,0.00'),
    };

    if (config[key] === undefined || Number(config[key]) === 0) {
      return '-';
    }

    return config[key];
  };

  const orderDone = [
    {
      key: 'price',
      label: 'Price',
      value: <Text align="right">{generateOrderDone('price', data)}</Text>,
    },
    {
      key: 'lot',
      label: 'Lot',
      value: <Text align="right">{generateOrderDone('lot', data)}</Text>,
    },
    {
      key: 'investmentDone',
      label: (
        <Text>
          {generateTitleBasedOnOrder('investmentDone', action.toLowerCase())}
        </Text>
      ),
      value: (
        <Text align="right">{generateOrderDone('investmentDone', data)}</Text>
      ),
    },
    {
      key: 'brokerFee',
      label: 'Broker Fee',
      value: <Text align="right">{generateOrderDone('brokerFee', data)}</Text>,
    },
    {
      key: 'exchangeFee',
      label: 'Exchange Fee',
      value: (
        <Text align="right">{generateOrderDone('exchangeFee', data)}</Text>
      ),
    },
    {
      key: 'infestmentFeeDone',
      label: (
        <Text>
          {generateTitleBasedOnOrder('investmentFeeDone', action.toLowerCase())}
        </Text>
      ),
      value: (
        <Text align="right">
          {generateOrderDone('infestmentFeeDone', data)}
        </Text>
      ),
    },
  ];

  if (showRealized && action === ORDER_TYPE.SELL) {
    orderDone.push({
      key: 'realized',
      label: <Text family="bold">Realized {isLoss ? 'Loss' : 'Gain'}</Text>,
      value: (
        <Realized
          total={totalRealized}
          percentage={realizedPercentage}
          isLoss={isLoss}
        />
      ),
    });
  }

  const showNonSyariahBanner = useMemo(
    () =>
      isUserSyariah &&
      !isStockSyariah &&
      action === ORDER_TYPE.BUY &&
      ![OrderStatus.Rejected, OrderStatus.Withdrawn].includes(
        status as OrderStatus,
      ),
    [isUserSyariah, isStockSyariah, action, status],
  );

  return (
    <DetailModal
      visible={isDetaillVisible}
      onCancel={handleModalCancel}
      title={`${action} ${symbol}`}
      symbol={symbol}
      prefix={<Prefix status={status} message={message} />}
      suffix={
        <ModalButtonContainerSecondary>
          {data?.cancel_action && (
            <DetailOrderButton variant="cancel" onClick={onCancelOrder}>
              Cancel Order
            </DetailOrderButton>
          )}
          {data?.amend_action && (
            <DetailOrderButton variant="amend" onClick={handleAmendOrderClick}>
              Amend
            </DetailOrderButton>
          )}
        </ModalButtonContainerSecondary>
      }
    >
      <If condition={isDayTrade}>
        <DayTradeBanner
          label={day_trade_force_sell_event}
          timeOpen={time_open}
        />
      </If>
      <If condition={showNonSyariahBanner}>
        {status === OrderStatus.Match ? (
          <NonSyariahBanner>
            Saham telah keluar dari <span>Daftar Efek Syariah</span>. Segera
            Jual saham tersebut dari portfolio kamu.
          </NonSyariahBanner>
        ) : (
          <NonSyariahBanner>
            Saham yang kamu order sudah keluar dari{' '}
            <span>Daftar Efek Syariah.</span> Silakan batalkan order kamu.
          </NonSyariahBanner>
        )}
      </If>
      <OrderModalInfoTable
        dataSource={orderInfo}
        title={`${action.toLowerCase()} Order`}
      />
      <OrderModalInfoTable
        dataSource={orderDone}
        title={`${action.toLowerCase()} Done`}
      />
      <OrderModalInfoTable dataSource={generatePorfolioInfo()} />
      {isShowShareButton && (
        <ShareTradeButton type="primary" onClick={handleShareTradeClick} block>
          Share Trade
        </ShareTradeButton>
      )}
    </DetailModal>
  );
};

export default OrderDetailModal;
